import { z } from 'zod'

/**
 * Zod schema for transient stay form validation.
 * Used by both drawer (full) and inline (quick-edit) variants.
 * Includes cross-field refinement: check_out must be after check_in.
 */
export const transientStayFormSchema = z
  .object({
    location_id: z.string().min(1, 'Location is required'),
    client_id: z.string().min(1, 'Client is required'),
    aircraft_id: z.string().min(1, 'Aircraft is required'),
    unit_id: z.string().optional().or(z.literal('')),
    check_in: z.string().min(1, 'Check in is required'),
    expected_check_out: z.string().min(1, 'Check out is required'),
    actual_check_out: z.string().optional().nullable(),
    booking_type: z.enum(['guaranteed', 'flex'] as const),
    status: z.enum([
      'inquiry',
      'reserved',
      'confirmed',
      'checked_in',
      'checked_out',
      'cancelled',
      'no_show',
    ] as const),
    source: z
      .enum([
        'phone',
        'email',
        'integration',
        'customer_portal',
        'walk_in',
        'quick_fit',
      ] as const)
      .optional()
      .nullable(),
    rate_type: z
      .enum(['flat', 'per_sqft', 'per_night'] as const)
      .optional()
      .nullable(),
    rate_amount: z.coerce.number().optional().nullable(),
    discount_amount: z.coerce.number().optional().nullable(),
    discount_reason: z.string().optional().nullable(),
    tax_amount: z.coerce.number().optional().nullable(),
    notes: z.string().optional().nullable(),
    special_requests: z.string().optional().nullable(),
  })
  .refine(
    (data) => {
      if (!data.check_in || !data.expected_check_out) return true
      return new Date(data.expected_check_out) > new Date(data.check_in)
    },
    {
      message: 'Check out must be after check in',
      path: ['expected_check_out'],
    }
  )

// Input type — used for form defaultValues
export type TransientStayFormInput = z.input<typeof transientStayFormSchema>

// Output type — after Zod parsing
export type TransientStayFormValues = z.output<typeof transientStayFormSchema>

// Default values for new transient stay form
export const transientStayFormDefaults: TransientStayFormInput = {
  location_id: '',
  client_id: '',
  aircraft_id: '',
  unit_id: '',
  check_in: '',
  expected_check_out: '',
  actual_check_out: null,
  booking_type: 'guaranteed',
  status: 'inquiry',
  source: 'phone',
  rate_type: 'per_night',
  rate_amount: null,
  discount_amount: null,
  discount_reason: null,
  tax_amount: null,
  notes: null,
  special_requests: null,
}
